
import React, {FC, PropsWithChildren, useCallback, useMemo, useRef} from 'react';
import {obs} from 'utils/observer';
import {hasGPC, hasOptOutCookie, readOptOut, writeOptOut} from 'utils/privacy';

import {useSetPrivacy} from './mutation';

export type AccountPrivacy = {
	id: string;
	doNotSellOrShare: boolean;
};

export type PrivacyContextValue = {
	syncAccount: (user: AccountPrivacy | null) => void;
};

const defaultValue: PrivacyContextValue = {
	syncAccount: () => {},
};

const PrivacyContext = React.createContext<PrivacyContextValue>(defaultValue);

export const usePrivacy = () => React.useContext(PrivacyContext);

export const PrivacyProvider: FC<PropsWithChildren> = ({children}) => {
	const syncedUserId = useRef<string | null>(null);
	const [mutate] = useSetPrivacy();

	// Sync account and browser once per login; an opt-out on either side wins
	const syncAccount = useCallback((user: AccountPrivacy | null) => {
		if (!user) {
			syncedUserId.current = null;
			return;
		}
		if (syncedUserId.current === user.id) {
			return;
		}
		syncedUserId.current = user.id;

		if (user.doNotSellOrShare && !hasOptOutCookie()) {
			writeOptOut();
			// Stop analytics now rather than on its next event
			obs.stop();
		} else if (!user.doNotSellOrShare && readOptOut()) {
			mutate({
				variables: {
					doNotSellOrShare: true,
					source: hasGPC() ? 'GPC' : 'WEB',
				},
			}).catch(() => {
				// Allow a retry on the next auth change
				syncedUserId.current = null;
			});
		}
	}, [mutate]);

	const value = useMemo<PrivacyContextValue>(
		() => ({syncAccount}),
		[syncAccount],
	);

	return (
		<PrivacyContext.Provider value={value}>
			{children}
		</PrivacyContext.Provider>
	);
};
